import { yupResolver } from "@hookform/resolvers/yup";
import FormButtons, { FormButtonInterface } from "components/forms/buttons";
import { SubmitButton } from "components/forms/buttons.utils";
import Form from "components/forms/form";
import Datepicker from "components/forms/inputs/datepicker";
import Dropdown from "components/forms/inputs/dropdown";
import { OptionInterface } from "components/forms/inputs/dropdown.styles";
import Col from "components/grid/col";
import Row from "components/grid/row";
import { faFilePdf } from "components/icon-library";
import { List } from "models/list";
import { Fragment, FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { downloadPdf } from "lib/base64";
import Checkbox from "components/forms/inputs/checkbox";
import { FormValues } from "lib/form";
import { parse } from "lib/locale";
import { useLocale } from "stores/hooks/locale";
import dayjs from "dayjs";
import {
  adminDoGetExportTimerangePdf,
  adminDoGetExportTimestampPdf,
} from "services/admin/list-export";
import { useTranslations } from "stores/hooks/translation";

interface PDFFormProps {
  list: List;
}

const PDFForm: FunctionComponent<PDFFormProps> = ({ list }) => {
  const locale = useLocale();
  const translations = useTranslations();

  const schema = yup
    .object({
      reportType: yup.string(),
      timestamp: yup.date().validateRequired(),
      timerange_from: yup.date(),
      timerange_to: yup.date().nullable(),
      filter: yup.string().validateRequired(),
      cover: yup.boolean(),
      user_per_page: yup.boolean(),
    })
    .required();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      reportType: "timestamp",
      timestamp: new Date(),
      timerange_from: dayjs().subtract(1, "month").toDate(),
      timerange_to: null,
      filter: "all",
      cover: true,
      user_per_page: false,
    },
  });

  const doPdf = async (values: FormValues) => {
    const {
      reportType,
      timestamp,
      filter,
      user_per_page,
      timerange_from,
      timerange_to,
      cover,
    } = values as any;
    let base64: string = "";
    switch (reportType) {
      case "timestamp":
        base64 = await adminDoGetExportTimestampPdf(list.uuid, translations, {
          timestamp,
          filter,
          cover,
          user_per_page,
        });
        break;
      case "timerange":
        base64 = await adminDoGetExportTimerangePdf(list.uuid, translations, {
          timerange_from,
          timerange_to,
          cover,
          user_per_page,
        });
        break;
      default:
        throw new Error("Unknown report type");
    }

    downloadPdf(`${parse(list.name, locale)}.pdf`, base64);
  };

  const options: OptionInterface[] = [
    { value: "all", text: "Kaikki ilmoitusvelvolliset" },
  ];

  const buttons: FormButtonInterface[] = [
    { ...SubmitButton, text: "Lataa PDF", postIcon: faFilePdf, align: "left" },
  ];

  const reportTypeOptions: OptionInterface[] = [
    { value: "timestamp", text: "Normaali raportti" },
    { value: "timerange", text: "Muutokset aikavälillä" },
  ];

  const { reportType } = methods.getValues();
  return (
    <Form methods={methods} onSubmit={doPdf}>
      <Row>
        <Col>
          <Dropdown
            options={reportTypeOptions}
            name="reportType"
            label="Raporttityyppi"
          />
        </Col>
      </Row>

      {/* filters for the default report */}
      {reportType === "timestamp" && (
        <Fragment>
          <Row>
            <Col>
              <Datepicker
                required
                time
                name="timestamp"
                label="Luo raportti ajanhetkestä"
              />
            </Col>
            <Col>
              <Dropdown
                required
                name="filter"
                label="Raportille valittavat ilmoitusvelvolliset"
                options={options}
              />
            </Col>
          </Row>
        </Fragment>
      )}

      {/* filters for the timerange report */}
      {reportType === "timerange" && (
        <Fragment>
          <Row>
            <Col>
              <Datepicker required time name="timerange_from" label="Alkaen" />
            </Col>
            <Col>
              <Datepicker time name="timerange_to" label="Päättyen" />
            </Col>
          </Row>
        </Fragment>
      )}

      {/* shared options */}
      <Fragment>
        <Row>
          <Col>
            <Checkbox
              name="cover"
              label="Lisää raporttiin kansilehti, jossa raportin perustiedot."
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="user_per_page"
              label="Raportoi jokainen ilmoitusvelvollinen omalle sivulleen."
            />
          </Col>
        </Row>
      </Fragment>

      <FormButtons buttons={buttons} />
    </Form>
  );
};

export default PDFForm;

/*
- Nimi, luottamus- ja virkatehtävä, tila
- Pitää olla kompakti!!!
- Jos omassa tabissaan, niin suodatustyökalut pitää olla näkyvissä
- Manuaalisesti tiettyjen henkilöiden valinta
- Tilavalinta
- Aikavalitsin (päivityksiä x sisään) (pvm valitsin riittää)
*/
